import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { Grid, List, ListItem, useTheme } from '@mui/material';
import { ListSection } from '@/types/sections/ListSection';
import { CardHighlightsItem } from '@/types/CardHighlightsItem';
import { Icon } from '@/components/Icon';
import { getIconName } from '@/components/Icon/Icon';

type ListProps = BoxProps & {
  section: ListSection;
};

export default function ListComponent({ sx, section, ...other }: ListProps) {
  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        {section?.small && (
          <m.div
            variants={varFade('inDown')}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div
            variants={varFade('inUp')}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}>
            <Typography
              variant="h3"
              sx={{ mx: 'auto', maxWidth: 640, my: 3, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div
            variants={varFade('inUp')}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}>
            <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <List sx={{ mt: 4, width: 1, p: 0 }}>
          {section?.cards?.map((item, index) => (
            <m.div
              key={`${section.uniqueId}-list-item-${index}`}
              variants={varFade('inUp')}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.3 }}
              style={{ width: '100%' }}>
              <ListItem
                disableGutters
                sx={{
                  p: 0,
                  mb: 3,
                  width: 1,
                  maxWidth: 1,
                  display: 'block',
                  boxSizing: 'border-box',
                }}>
                <Item
                  index={index}
                  item={{
                    id: `${section.uniqueId}-list-item-item-${index}`,
                    ...item,
                  }}
                />
              </ListItem>
            </m.div>
          ))}
        </List>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type ItemProps = {
  index: number;
  item: { id: string } & CardHighlightsItem;
};

function Item({ item, index }: ItemProps) {
  const theme = useTheme();
  const isEven = index % 2 === 1; // Even row (odd index)

  // Image column: height tracks width (max height = width × 75% via aspect-ratio 4/3 → h = w×3/4).
  const ImageColumn = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        position: 'relative',
        width: 1,
      }}>
      <Box
        sx={{
          position: 'relative',
          width: item.icon ? '88%' : 1,
          maxWidth: 1,
          mx: 'auto',
          aspectRatio: '4 / 3',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            position: 'absolute',
            left: '5%',
            top: '8%',
            zIndex: 1,
          }}>
          {item.icon && (
            <Icon
              name={getIconName(item.icon, 'unknown')}
              size="32"
              color={theme.palette.primary.main}
            />
          )}
        </Box>

        <Box
          component="img"
          src={item.image?.url || ''}
          alt={item.title || 'Feature image'}
          sx={{
            width: 1,
            height: 1,
            display: 'block',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </Box>
    </Box>
  );

  // Info Column Component
  const InfoColumn = () => (
    <Box
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        textAlign: 'left',
      }}>
      {item.word && (
        <Typography paddingBottom={1} variant="overline" sx={{ color: 'text.disabled' }}>
          {item.word}
        </Typography>
      )}

      {item.title && (
        <Typography variant="h4" sx={{ color: 'primary.main' }}>
          {item.title}
        </Typography>
      )}

      {item.subtitle && (
        <Typography
          variant="body2"
          sx={{
            mt: 0,
            mb: 2,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}>
          {item.subtitle}
        </Typography>
      )}

      {item?.text && <Typography sx={{ color: 'text.secondary' }}>{item?.text}</Typography>}

      {item?.highlights?.map((highlight, idx) => (
        <Box
          key={`list-highlight-${idx}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translateX(5px)',
            },
          }}>
          <Box sx={{ color: 'text.secondary', mr: 2, mt: 0 }}>
            <Icon name={getIconName(highlight.icon, 'unknown')} size="16" />
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              '& strong': { color: 'text.primary' },
            }}>
            {highlight.text || ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Info Column Component
  const InfoColumnText = () => (
    <Box
      sx={{
        p: 3,
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        textAlign: 'left',
      }}>
      {item.word && (
        <Typography paddingBottom={4} variant="overline" sx={{ color: 'text.disabled' }}>
          {item.word}
        </Typography>
      )}

      {item.title && (
        <Typography variant="h4" sx={{ color: 'primary.main', mb: 2 }}>
          {item.title}
        </Typography>
      )}

      {item.subtitle && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}>
          {item.subtitle}
        </Typography>
      )}

      {item?.text && <Typography sx={{ color: 'text.secondary', mb: 2 }}>{item?.text}</Typography>}

      {item?.highlights?.map((highlight, idx) => (
        <Box
          key={`list-highlight-${idx}`} // Use unique ID or fallback to index
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translateX(5px)',
            },
          }}>
          <Box sx={{ color: 'text.secondary', mr: 2, mt: 0 }}>
            <Icon name={getIconName(highlight.icon, 'unknown')} size="16" />
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              '& strong': { color: 'text.primary' },
            }}>
            {highlight.text || ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Grid
      container
      direction={isEven ? 'row-reverse' : 'row'}
      alignItems="stretch"
      sx={{
        borderRadius: 0,
        overflow: 'hidden',
        width: 1,
      }}>
      {/* First Column - 50% width */}
      <Grid size={{ xs: 12, md: 6 }}>
        <ImageColumn />
      </Grid>

      {/* Second Column - 50% width — vertically center copy next to image (md+) */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <InfoColumnText />
      </Grid>
    </Grid>
  );
}
